import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subject, catchError, distinctUntilChanged, finalize, map, merge, of, startWith, switchMap, tap } from 'rxjs';

import { IActiveCampaign, ICampaignObjectiveProgress, PromotionsService } from 'core';
import { KorviuLoaderComponent, KorviuNoDataComponent } from 'shared';
import { RefreshOnLanguageChangeDirective } from 'shared/directives/refresh-on-language-change.directive';

@Component({
  selector: 'app-active-missions',
  imports: [KorviuLoaderComponent, KorviuNoDataComponent, RouterLink, TranslatePipe],
  templateUrl: './active-missions.component.html',
  styleUrl: './active-missions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [RefreshOnLanguageChangeDirective]
})
export class ActiveMissionsComponent implements OnInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #promotionsService = inject(PromotionsService);
  readonly #translateService = inject(TranslateService);
  readonly #reloadCampaign$ = new Subject<void>();

  readonly activeCampaign = signal<IActiveCampaign | null>(null);
  readonly isLoading = signal<boolean>(false);
  readonly loadError = signal<boolean>(false);
  readonly objectiveProgress = computed<ICampaignObjectiveProgress | null>(
    () => this.activeCampaign()?.objective_progress ?? null
  );
  readonly progress = computed(() => {
    const objectiveProgress = this.objectiveProgress();

    if (!objectiveProgress?.target) {
      return 0;
    }

    return Math.min(100, Math.max(0, (objectiveProgress.current / objectiveProgress.target) * 100));
  });

  public ngOnInit(): void {
    merge(
      this.#reloadCampaign$,
      this.#translateService.onLangChange.pipe(
        map(event => event.lang),
        startWith(this.#translateService.currentLang || this.#translateService.defaultLang || 'es'),
        distinctUntilChanged()
      )
    )
      .pipe(
        switchMap(() => {
          this.activeCampaign.set(null);
          this.isLoading.set(true);
          this.loadError.set(false);

          return this.#promotionsService.getActiveCampaigns({ page: 1, pageSize: 10 }).pipe(
            tap(response => this.activeCampaign.set(response.results[0] ?? null)),
            catchError(() => {
              this.loadError.set(true);
              return of(null);
            }),
            finalize(() => this.isLoading.set(false))
          );
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public loadActiveCampaign(): void {
    this.#reloadCampaign$.next();
  }
}
